import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, from } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { FileService, FilesResponse } from './file.service';

interface CacheEntry {
  url: SafeResourceUrl;
  rawUrl: string;
  refCount: number;
  lastUsed: number;
  langActuallyServed: 'en' | 'hi';
}

const REVOKE_GRACE_MS = 10_000;
const MAX_CACHE_ENTRIES = 25;

/**
 * Lazy, ref-counted blob URL cache for bundle file viewers.
 *
 * Replaces the old eager `loadPreviewsForFilteredBundles` flow that fired
 * 5×N HTTP requests on first paint. Now each viewer fetches on mount and
 * releases on unmount; the service hands out cached SafeResourceUrls when
 * available and revokes object URLs after a grace period to avoid thrash.
 */
@Injectable({ providedIn: 'root' })
export class BundleViewerService {
  private fileService = inject(FileService);
  private sanitizer = inject(DomSanitizer);

  private cache = new Map<string, CacheEntry>();
  private inFlight = new Map<string, Observable<{ url: SafeResourceUrl; langActuallyServed: 'en' | 'hi' }>>();
  private revokeTimers = new Map<string, ReturnType<typeof setTimeout>>();

  fetchAsBlobUrl(
    bundle: FilesResponse,
    fileType: string,
    lang: 'en' | 'hi'
  ): Observable<{ url: SafeResourceUrl; langActuallyServed: 'en' | 'hi' }> {
    const resolved = this.fileService.resolveFileKey(bundle, fileType, lang);
    if (!resolved) {
      throw new Error(`No file for type=${fileType} lang=${lang} in bundle ${bundle.id}`);
    }
    const langActuallyServed: 'en' | 'hi' = /_hindi$/i.test(resolved) ? 'hi' : 'en';
    const key = this.makeKey(bundle.id, resolved);

    const cached = this.cache.get(key);
    if (cached) {
      cached.refCount += 1;
      cached.lastUsed = Date.now();
      this.cancelRevoke(key);
      return from(Promise.resolve({ url: cached.url, langActuallyServed: cached.langActuallyServed }));
    }

    const inFlight = this.inFlight.get(key);
    if (inFlight) return inFlight;

    const obs = this.fileService.downloadFileByType(bundle.id, resolved).pipe(
      map(blob => {
        const rawUrl = URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(rawUrl);
        const entry: CacheEntry = {
          url: safeUrl,
          rawUrl,
          refCount: 1,
          lastUsed: Date.now(),
          langActuallyServed,
        };
        this.cache.set(key, entry);
        this.inFlight.delete(key);
        this.evictIfOverCap();
        return { url: safeUrl, langActuallyServed };
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.inFlight.set(key, obs);
    return obs;
  }

  release(bundle: FilesResponse, fileType: string, lang: 'en' | 'hi'): void {
    const resolved = this.fileService.resolveFileKey(bundle, fileType, lang);
    if (!resolved) return;
    const key = this.makeKey(bundle.id, resolved);
    const entry = this.cache.get(key);
    if (!entry) return;
    entry.refCount = Math.max(0, entry.refCount - 1);
    if (entry.refCount === 0) {
      this.scheduleRevoke(key);
    }
  }

  // Used by tests / debug logging only.
  size(): number {
    return this.cache.size;
  }

  private makeKey(bundleId: number, resolvedFileKey: string): string {
    return `${bundleId}::${resolvedFileKey}`;
  }

  private scheduleRevoke(key: string): void {
    this.cancelRevoke(key);
    const t = setTimeout(() => {
      const entry = this.cache.get(key);
      if (!entry || entry.refCount > 0) return;
      try { URL.revokeObjectURL(entry.rawUrl); } catch { /* noop */ }
      this.cache.delete(key);
      this.revokeTimers.delete(key);
    }, REVOKE_GRACE_MS);
    this.revokeTimers.set(key, t);
  }

  private cancelRevoke(key: string): void {
    const t = this.revokeTimers.get(key);
    if (t) {
      clearTimeout(t);
      this.revokeTimers.delete(key);
    }
  }

  private evictIfOverCap(): void {
    if (this.cache.size <= MAX_CACHE_ENTRIES) return;
    // LRU evict among entries with refCount === 0
    const candidates = Array.from(this.cache.entries())
      .filter(([, v]) => v.refCount === 0)
      .sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    while (this.cache.size > MAX_CACHE_ENTRIES && candidates.length) {
      const [key, entry] = candidates.shift()!;
      this.cancelRevoke(key);
      try { URL.revokeObjectURL(entry.rawUrl); } catch { /* noop */ }
      this.cache.delete(key);
    }
  }
}
